import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { TrendingUp, Calendar, Layers, Wallet, Flame, Compass, ChevronRight, TrendingDown, ArrowRightLeft, Tag, ArrowUpRight, ArrowDownRight, Minus, AlertTriangle, RotateCw } from "lucide-react";
import { formatMoney } from "@/lib/currency";
import { Link } from "react-router-dom";
import { toast } from "sonner";

// Small helper: standard error message extractor so every tab's retry surfaces
// the actual reason (network, 500, timeout) instead of a silent loading state.
const errMessage = (e) =>
  e?.response?.data?.detail ||
  e?.message ||
  "couldn't reach the studio server.";

function LoadingOrError({ error, onRetry, loadingLabel, testid }) {
  if (error) {
    return (
      <div className="bg-linen border border-red-700/30 p-6 flex flex-wrap items-center gap-4" data-testid={testid || "analytics-error"}>
        <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="editorial-label text-red-800">couldn&apos;t load this view</p>
          <p className="text-sm text-espresso mt-1 break-words">{error}</p>
        </div>
        <button
          onClick={onRetry}
          data-testid={`${testid || "analytics"}-retry`}
          className="bg-brass hover:bg-espresso text-ivory px-4 py-2 text-xs tracking-[0.25em] uppercase flex items-center gap-2"
        >
          <RotateCw className="w-3 h-3" /> retry
        </button>
      </div>
    );
  }
  return <p className="editorial-label">{loadingLabel}</p>;
}

export default function AdminAnalytics() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [age, setAge] = useState(null);
  const [sourcing, setSourcing] = useState(null);
  const [brand, setBrand] = useState(null);
  const [channel, setChannel] = useState(null);
  const [errors, setErrors] = useState({}); // { overview: msg, age: msg, ... }

  const setError = (key, msg) => setErrors((prev) => ({ ...prev, [key]: msg }));
  const clearError = (key) => setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });

  const loadOverview = useCallback(() => {
    clearError("overview");
    api.get("/analytics/overview").then((r) => setOverview(r.data)).catch((e) => setError("overview", errMessage(e)));
  }, []);
  const loadAge = useCallback(() => {
    clearError("age");
    api.get("/analytics/inventory-age").then((r) => setAge(r.data)).catch((e) => setError("age", errMessage(e)));
  }, []);
  const loadSourcing = useCallback(() => {
    clearError("sourcing");
    api.get("/analytics/sourcing-roi").then((r) => setSourcing(r.data)).catch((e) => setError("sourcing", errMessage(e)));
  }, []);
  const loadBrand = useCallback(() => {
    clearError("brand");
    api.get("/analytics/brand-trend").then((r) => setBrand(r.data)).catch((e) => setError("brand", errMessage(e)));
  }, []);
  const loadChannel = useCallback(() => {
    clearError("channel");
    api.get("/analytics/channel-attribution").then((r) => setChannel(r.data)).catch((e) => setError("channel", errMessage(e)));
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => {
    if (tab === "age" && !age && !errors.age) loadAge();
    if (tab === "sourcing" && !sourcing && !errors.sourcing) loadSourcing();
    if (tab === "brand" && !brand && !errors.brand) loadBrand();
    if (tab === "channel" && !channel && !errors.channel) loadChannel();
  }, [tab, age, sourcing, brand, channel, errors, loadAge, loadSourcing, loadBrand, loadChannel]);

  const tabs = [
    { id: "overview", label: "overview", icon: <TrendingUp className="w-3 h-3" /> },
    { id: "age", label: "age of inventory", icon: <Flame className="w-3 h-3" /> },
    { id: "sourcing", label: "sourcing roi", icon: <Compass className="w-3 h-3" /> },
    { id: "brand", label: "brand trend", icon: <TrendingUp className="w-3 h-3" /> },
    { id: "channel", label: "channel attribution", icon: <ArrowRightLeft className="w-3 h-3" /> },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-analytics" className="max-w-6xl">
        <span className="editorial-label">decisions · analytics</span>
        <h1 className="font-serif text-5xl lowercase text-charcoal mt-3 mb-2">where the house grows.</h1>
        <p className="text-espresso max-w-2xl mb-8">a clear-eyed view of what sells, how fast, where to source next, and which pieces are eating your working capital.</p>

        {/* Tab bar */}
        <div className="flex flex-wrap border-b border-taupe/40 mb-10 -mx-1" data-testid="analytics-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-brass text-charcoal" : "border-transparent text-taupe hover:text-espresso"
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (overview
          ? <OverviewPanel data={overview} />
          : <LoadingOrError error={errors.overview} onRetry={loadOverview} loadingLabel="loading…" testid="err-overview" />)}
        {tab === "age" && (age
          ? <AgePanel data={age} reload={loadAge} />
          : <LoadingOrError error={errors.age} onRetry={loadAge} loadingLabel="loading age heatmap…" testid="err-age" />)}
        {tab === "sourcing" && (sourcing
          ? <SourcingPanel data={sourcing} />
          : <LoadingOrError error={errors.sourcing} onRetry={loadSourcing} loadingLabel="loading sourcing roi…" testid="err-sourcing" />)}
        {tab === "brand" && (brand
          ? <BrandTrendPanel data={brand} />
          : <LoadingOrError error={errors.brand} onRetry={loadBrand} loadingLabel="loading brand trend…" testid="err-brand" />)}
        {tab === "channel" && (channel
          ? <ChannelPanel data={channel} />
          : <LoadingOrError error={errors.channel} onRetry={loadChannel} loadingLabel="loading channel attribution…" testid="err-channel" />)}
      </div>
    </AdminLayout>
  );
}

// -------------------- Overview --------------------

function OverviewPanel({ data }) {
  const s = data.summary;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        <Card testid="kpi-sell-through" icon={<TrendingUp />} label="sell-through" value={`${s.sell_through_rate}%`} sub={`${s.sold_pieces} of ${s.total_pieces} pieces`} />
        <Card testid="kpi-days-on-shelf" icon={<Calendar />} label="avg days on shelf" value={s.avg_days_on_shelf} sub="time from sourcing to sold" />
        <Card testid="kpi-aov" icon={<Wallet />} label="average order value" value={formatMoney(s.average_order_value)} sub={`${s.total_orders} orders`} />
        <Card testid="kpi-unsold" icon={<Layers />} label="awaiting buyer" value={s.unsold_pieces} sub="current inventory" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <PerfTable testid="perf-brand" title="top brands" rows={data.by_brand} />
        <PerfTable testid="perf-type" title="top types" rows={data.by_type} />
        <PerfTable testid="perf-color" title="top colours" rows={data.by_color} />
        <PerfTable testid="perf-style" title="top styles" rows={data.by_style} />
      </div>
    </>
  );
}

function Card({ testid, icon, label, value, sub }) {
  return (
    <div data-testid={testid} className="bg-linen border border-taupe/50 p-6">
      <div className="flex items-center justify-between mb-3 text-brass">{icon}<span className="editorial-label">{label}</span></div>
      <p className="font-serif text-4xl text-brass">{value}</p>
      <p className="text-xs text-espresso mt-2">{sub}</p>
    </div>
  );
}

function PerfTable({ testid, title, rows }) {
  return (
    <div data-testid={testid} className="bg-linen border border-taupe/50 p-6">
      <h3 className="font-serif text-2xl lowercase text-charcoal mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="editorial-label">no data yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-taupe">
              <th className="editorial-label py-2">key</th>
              <th className="editorial-label py-2 text-right">sold</th>
              <th className="editorial-label py-2 text-right">sell-through</th>
              <th className="editorial-label py-2 text-right">revenue</th>
              <th className="editorial-label py-2 text-right">avg days</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-taupe/30">
                <td className="py-2 font-serif text-base lowercase text-charcoal">{(r.key || "").toLowerCase()}</td>
                <td className="py-2 text-right text-charcoal tabular-nums">{r.sold} / {r.total}</td>
                <td className="py-2 text-right text-brass tabular-nums">{r.sell_through}%</td>
                <td className="py-2 text-right text-charcoal tabular-nums">{formatMoney(r.revenue)}</td>
                <td className="py-2 text-right text-taupe tabular-nums">{r.avg_days_on_shelf || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// -------------------- Age of Inventory --------------------

function AgePanel({ data, reload }) {
  const bands = data.bands || [];
  const totalUnsold = data.total_unsold || 0;

  // Count of pieces with a live reprice recommendation (critical/dead + non-breakeven).
  const criticalBand = bands.find((b) => b.key === "critical");
  const deadBand = bands.find((b) => b.key === "dead");
  const repricePool = [
    ...(criticalBand?.pieces || []).filter((p) => p.recommendation),
    ...(deadBand?.pieces || []).filter((p) => p.recommendation),
  ];

  const [bulkRunning, setBulkRunning] = useState(false);

  const bulkReprice = async () => {
    if (!repricePool.length) return;
    if (!window.confirm(
      `Apply the recommended reprice to ${repricePool.length} critical/dead piece${repricePool.length === 1 ? "" : "s"}?\n\n` +
      `Each piece's sale price will be reduced (never below breakeven), original price will be stamped so the storefront shows a strike-through, and the change will be logged to reprice history.`
    )) return;
    setBulkRunning(true);
    let ok = 0, err = 0;
    for (const p of repricePool) {
      try {
        await api.post(`/inventory/${p.id}/reprice`, {});
        ok++;
      } catch {
        err++;
      }
    }
    toast[err > 0 ? "warning" : "success"](`Repriced ${ok} · ${err ? `${err} failed` : "all done"}.`);
    reload && reload();
    setBulkRunning(false);
  };

  return (
    <div data-testid="age-panel">
      {/* Headline */}
      <div className="grid sm:grid-cols-2 gap-5 mb-8">
        <Card testid="kpi-age-unsold" icon={<Layers />} label="pieces on the floor" value={totalUnsold} sub="unsold + not discontinued" />
        <Card testid="kpi-age-capital" icon={<Wallet />} label="working capital tied up" value={formatMoney(data.total_capital_invested || 0)} sub="cost of unsold inventory" />
      </div>

      {/* Bulk reprice */}
      {repricePool.length > 0 && (
        <div className="bg-linen border border-brass/40 p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="bulk-reprice-panel">
          <div>
            <p className="font-serif text-lg lowercase text-charcoal flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-brass" />
              {repricePool.length} piece{repricePool.length === 1 ? "" : "s"} eligible for reprice
            </p>
            <p className="text-xs text-espresso mt-1">critical (-15%) + dead (-25%), never below breakeven. every reprice is logged and shows on the storefront as a strike-through.</p>
          </div>
          <button
            onClick={bulkReprice}
            disabled={bulkRunning}
            data-testid="bulk-reprice-btn"
            className="bg-brass hover:bg-espresso text-ivory px-5 py-3 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
          >
            <Tag className="w-3 h-3" />
            {bulkRunning ? "applying…" : "reprice all critical + dead"}
          </button>
        </div>
      )}

      {/* Distribution bar */}
      <div className="bg-linen border border-taupe/50 p-6 mb-8" data-testid="age-distribution">
        <h3 className="font-serif text-2xl lowercase text-charcoal mb-4">distribution</h3>
        <div className="w-full h-8 flex overflow-hidden border border-taupe/30 rounded-sm">
          {bands.map((b) => {
            const pct = totalUnsold ? (b.count / totalUnsold) * 100 : 0;
            return pct > 0 ? (
              <div
                key={b.key}
                title={`${b.key} · ${b.count} pieces · ${pct.toFixed(1)}%`}
                style={{ width: `${pct}%`, backgroundColor: b.colour }}
                data-testid={`age-band-slice-${b.key}`}
                className="h-full transition-all"
              />
            ) : null;
          })}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-xs">
          {bands.map((b) => (
            <div key={b.key} className="flex items-center gap-2 text-espresso">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: b.colour }} />
              <span className="lowercase">{b.key} · {b.min}-{b.max >= 10000 ? "∞" : b.max}d</span>
              <span className="text-taupe">({b.count})</span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-band tables */}
      <div className="space-y-8">
        {bands.map((b) => (
          <AgeBand key={b.key} band={b} reload={reload} />
        ))}
      </div>
    </div>
  );
}

function AgeBand({ band, reload }) {
  const [busyId, setBusyId] = useState(null);

  const reprice = async (piece) => {
    if (!piece.recommendation) return;
    const r = piece.recommendation;
    const label = r.capped_by_breakeven ? "at breakeven (further drop blocked)" : `-${r.drop_pct}%`;
    if (!window.confirm(
      `Reprice "${piece.title}"?\n\n` +
      `$${piece.sale_price.toFixed(2)} → $${r.new_price.toFixed(2)} (${label})\n` +
      `Breakeven guard: $${r.breakeven.toFixed(2)}\n\n` +
      `The storefront will show a strike-through price. Change is logged to reprice history.`
    )) return;
    setBusyId(piece.id);
    try {
      await api.post(`/inventory/${piece.id}/reprice`, {});
      toast.success("repriced.");
      reload && reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "reprice failed.");
    } finally {
      setBusyId(null);
    }
  };

  if (!band.count) return null;
  return (
    <div className="bg-linen border-l-4 p-6" style={{ borderLeftColor: band.colour }} data-testid={`age-band-${band.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <h4 className="font-serif text-2xl lowercase text-charcoal">
            {band.key} · <span className="text-taupe">{band.min}-{band.max >= 10000 ? "∞" : band.max} days</span>
          </h4>
          <p className="text-xs text-espresso mt-1">{band.action}</p>
        </div>
        <div className="text-right">
          <p className="editorial-label">capital tied up</p>
          <p className="font-serif text-xl text-brass tabular-nums">{formatMoney(band.capital_invested)}</p>
          <p className="text-[0.65rem] text-taupe uppercase tracking-widest">{band.count} piece{band.count === 1 ? "" : "s"}</p>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-taupe">
            <th className="editorial-label py-2">piece</th>
            <th className="editorial-label py-2 text-right">days</th>
            <th className="editorial-label py-2 text-right">cost tied</th>
            <th className="editorial-label py-2 text-right">price</th>
            <th className="editorial-label py-2 text-right">reprice</th>
            <th className="editorial-label py-2"></th>
          </tr>
        </thead>
        <tbody>
          {band.pieces.slice(0, 25).map((p) => (
            <tr key={p.id} className="border-t border-taupe/20 hover:bg-ivory/40">
              <td className="py-2">
                <div className="flex items-center gap-3 min-w-0">
                  {p.photo ? (
                    <div className="w-10 h-10 border border-taupe/30 overflow-hidden flex-shrink-0 bg-ivory">
                      <img src={fileUrl(p.photo)} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 border border-taupe/30 bg-ivory flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="font-serif text-base text-charcoal truncate">{p.title.toLowerCase()}</p>
                    <p className="text-[0.65rem] uppercase tracking-widest text-taupe">
                      {p.sku || "—"}{p.brand ? ` · ${p.brand}` : ""}
                      {p.original_price > 0 && p.markdown_pct > 0 && (
                        <span className="ml-2 text-brass">· marked down -{p.markdown_pct}%</span>
                      )}
                    </p>
                  </div>
                </div>
              </td>
              <td className="py-2 text-right tabular-nums font-medium" style={{ color: band.colour }}>{Math.round(p.days_on_shelf)}</td>
              <td className="py-2 text-right text-taupe tabular-nums">{formatMoney(p.cost_tied_up)}</td>
              <td className="py-2 text-right tabular-nums">
                {p.original_price > 0 && p.original_price > p.sale_price ? (
                  <>
                    <span className="line-through text-taupe mr-1">{formatMoney(p.original_price)}</span>
                    <span className="text-brass">{formatMoney(p.sale_price)}</span>
                  </>
                ) : (
                  <span className="text-charcoal">{formatMoney(p.sale_price)}</span>
                )}
              </td>
              <td className="py-2 text-right tabular-nums text-xs">
                {p.recommendation ? (
                  <div>
                    <p className="text-charcoal">→ {formatMoney(p.recommendation.new_price)}</p>
                    <p className={p.recommendation.capped_by_breakeven ? "text-taupe" : "text-emerald-800"}>
                      -{p.recommendation.drop_pct}%
                      {p.recommendation.capped_by_breakeven && " · capped"}
                    </p>
                  </div>
                ) : (
                  <span className="text-taupe">—</span>
                )}
              </td>
              <td className="py-2 text-right">
                <div className="flex gap-2 justify-end">
                  {p.recommendation && (
                    <button
                      onClick={() => reprice(p)}
                      disabled={busyId === p.id}
                      data-testid={`reprice-${p.id}`}
                      className="text-brass hover:text-charcoal inline-flex items-center gap-1 text-xs tracking-widest uppercase disabled:opacity-50"
                    >
                      {busyId === p.id ? "…" : "apply"}
                    </button>
                  )}
                  <Link
                    to={`/admin/inventory/${p.id}`}
                    className="text-brass hover:text-charcoal inline-flex items-center gap-1 text-xs tracking-widest uppercase"
                    data-testid={`age-open-${p.id}`}
                  >
                    open <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {band.pieces.length > 25 && (
        <p className="editorial-label mt-3 text-taupe">+ {band.pieces.length - 25} more · showing 25 oldest</p>
      )}
    </div>
  );
}

// -------------------- Sourcing ROI --------------------

function SourcingPanel({ data }) {
  const vendors = data.vendors || [];
  const t = data.totals || {};

  // Best-ROI vendor (with at least 2 sold pieces to avoid single-sale outliers).
  const roiCandidates = vendors.filter((v) => v.sold >= 2);
  const bestRoi = roiCandidates.length
    ? roiCandidates.reduce((a, b) => (a.roi_pct >= b.roi_pct ? a : b))
    : null;

  return (
    <div data-testid="sourcing-panel">
      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <Card testid="sroi-vendor-count" icon={<Compass />} label="sourcing sources" value={t.vendor_count || 0} sub="distinct vendors" />
        <Card testid="sroi-acquired" icon={<Layers />} label="pieces sourced" value={t.acquired || 0} sub={`${t.sold || 0} sold`} />
        <Card testid="sroi-revenue" icon={<Wallet />} label="lifetime revenue" value={formatMoney(t.revenue || 0)} sub={`gross margin ${formatMoney(t.gross_margin || 0)}`} />
        <Card testid="sroi-best" icon={<TrendingUp />} label="best roi vendor" value={bestRoi ? `${bestRoi.roi_pct}%` : "—"} sub={bestRoi ? bestRoi.vendor.toLowerCase() : "need ≥2 sales / vendor"} />
      </div>

      {/* Vendor table */}
      <div className="bg-linen border border-taupe/50 p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-serif text-2xl lowercase text-charcoal">by vendor</h3>
          <span className="editorial-label">where to go shopping again</span>
        </div>
        {vendors.length === 0 ? (
          <p className="editorial-label">no source vendors logged yet — add <em>source vendor</em> on each piece to unlock this view.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-taupe">
                <th className="editorial-label py-2">vendor</th>
                <th className="editorial-label py-2 text-right">sold / acquired</th>
                <th className="editorial-label py-2 text-right">sell-through</th>
                <th className="editorial-label py-2 text-right">avg turn</th>
                <th className="editorial-label py-2 text-right">invested</th>
                <th className="editorial-label py-2 text-right">revenue</th>
                <th className="editorial-label py-2 text-right">margin</th>
                <th className="editorial-label py-2 text-right">roi</th>
                <th className="editorial-label py-2 text-right">exposed</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => {
                const isBest = bestRoi && v.vendor === bestRoi.vendor;
                return (
                  <tr key={v.vendor} className="border-t border-taupe/30" data-testid={`sroi-row-${v.vendor.replace(/\s+/g, "-").toLowerCase()}`}>
                    <td className="py-2 font-serif text-base lowercase text-charcoal">
                      {v.vendor.toLowerCase()}
                      {isBest && <span className="ml-2 text-[0.6rem] uppercase tracking-widest text-brass border border-brass/50 px-1.5 py-0.5">★ top roi</span>}
                    </td>
                    <td className="py-2 text-right tabular-nums text-charcoal">{v.sold} / {v.acquired}</td>
                    <td className="py-2 text-right tabular-nums text-brass">{v.sell_through}%</td>
                    <td className="py-2 text-right tabular-nums text-taupe">{v.avg_turn_days || "—"}d</td>
                    <td className="py-2 text-right tabular-nums text-charcoal">{formatMoney(v.invested)}</td>
                    <td className="py-2 text-right tabular-nums text-charcoal">{formatMoney(v.revenue)}</td>
                    <td className={`py-2 text-right tabular-nums font-medium ${v.gross_margin >= 0 ? "text-emerald-800" : "text-red-700"}`}>{formatMoney(v.gross_margin)}</td>
                    <td className={`py-2 text-right tabular-nums font-medium ${v.roi_pct >= 100 ? "text-emerald-800" : v.roi_pct >= 0 ? "text-charcoal" : "text-red-700"}`}>{v.roi_pct}%</td>
                    <td className="py-2 text-right tabular-nums text-taupe" title="capital still tied up in unsold pieces from this vendor">
                      {v.unsold > 0 ? `${v.unsold} · ${formatMoney(v.unsold_capital)}` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="editorial-label mt-4 text-taupe">roi = gross margin ÷ invested-in-sold-pieces. exposed = pieces still unsold from that vendor + their cost still on the books.</p>
      </div>
    </div>
  );
}

// -------------------- Brand Demand Trend --------------------

function BrandTrendPanel({ data }) {
  const quarters = data.quarters || [];
  const brands = data.brands || [];
  const rising = brands.filter((b) => b.direction === "rising");
  const cooling = brands.filter((b) => b.direction === "cooling");

  return (
    <div data-testid="brand-trend-panel">
      <div className="grid sm:grid-cols-3 gap-5 mb-8">
        <Card testid="brand-tracked" icon={<Layers />} label="brands tracked" value={brands.length} sub={`across ${quarters.length} quarters`} />
        <Card testid="brand-rising" icon={<ArrowUpRight />} label="rising demand" value={rising.length} sub="sell-through up >5pp" />
        <Card testid="brand-cooling" icon={<ArrowDownRight />} label="cooling demand" value={cooling.length} sub="sell-through down >5pp" />
      </div>

      <div className="bg-linen border border-taupe/50 p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-serif text-2xl lowercase text-charcoal">quarterly sell-through by brand</h3>
          <span className="editorial-label">what to keep buying · what to walk away from</span>
        </div>
        {brands.length === 0 ? (
          <p className="editorial-label">no brand data yet — add brand names on each piece.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-taupe">
                <th className="editorial-label py-2">brand</th>
                <th className="editorial-label py-2 text-right">pieces</th>
                {quarters.map((q) => (
                  <th key={q} className="editorial-label py-2 text-right">{q}</th>
                ))}
                <th className="editorial-label py-2 text-right">lifetime rev</th>
                <th className="editorial-label py-2 text-right">direction</th>
                <th className="editorial-label py-2"></th>
              </tr>
            </thead>
            <tbody>
              {brands.map((b) => (
                <tr key={b.brand} className="border-t border-taupe/30" data-testid={`brand-row-${b.brand.replace(/\s+/g, "-").toLowerCase()}`}>
                  <td className="py-2 font-serif text-base lowercase text-charcoal">{b.brand.toLowerCase()}</td>
                  <td className="py-2 text-right tabular-nums text-taupe">{b.total_pieces}</td>
                  {b.series.map((s) => (
                    <td key={s.quarter} className="py-2 text-right tabular-nums">
                      <span className={s.sell_through >= 50 ? "text-emerald-800 font-medium" : s.sell_through >= 20 ? "text-charcoal" : "text-taupe"}>
                        {s.sell_through}%
                      </span>
                      <span className="text-[0.6rem] text-taupe ml-1">({s.sold}/{s.acquired})</span>
                    </td>
                  ))}
                  <td className="py-2 text-right tabular-nums text-charcoal">{formatMoney(b.lifetime_revenue)}</td>
                  <td className="py-2 text-right">
                    <BrandTrendBadge dir={b.direction} />
                  </td>
                  <td className="py-2">
                    <Sparkline values={b.series.map((s) => s.sell_through)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="editorial-label mt-4 text-taupe">direction = last quarter&apos;s sell-through vs previous quarter (±5pp threshold). brands with &lt;3 pieces are grouped as &ldquo;— other&rdquo;.</p>
      </div>
    </div>
  );
}

function BrandTrendBadge({ dir }) {
  if (dir === "rising")
    return <span className="text-[0.65rem] uppercase tracking-widest text-emerald-800 border border-emerald-800/40 px-2 py-0.5 inline-flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> rising</span>;
  if (dir === "cooling")
    return <span className="text-[0.65rem] uppercase tracking-widest text-red-700 border border-red-700/40 px-2 py-0.5 inline-flex items-center gap-1"><ArrowDownRight className="w-3 h-3" /> cooling</span>;
  return <span className="text-[0.65rem] uppercase tracking-widest text-taupe border border-taupe/40 px-2 py-0.5 inline-flex items-center gap-1"><Minus className="w-3 h-3" /> steady</span>;
}

// Tiny inline SVG sparkline for the quarterly series.
function Sparkline({ values }) {
  const w = 80, h = 24;
  if (!values?.length) return null;
  const max = Math.max(...values, 10); // floor of 10 so a flat 0-line still shows
  const step = w / Math.max(1, values.length - 1);
  const points = values.map((v, i) => `${i * step},${h - (v / max) * (h - 4) - 2}`).join(" ");
  const last = values[values.length - 1];
  const prev = values.length >= 2 ? values[values.length - 2] : 0;
  const stroke = last > prev + 5 ? "#4b7f52" : last < prev - 5 ? "#a83a3a" : "#b8a884";
  return (
    <svg width={w} height={h} className="inline-block">
      <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={points} />
      {values.map((v, i) => (
        <circle key={i} cx={i * step} cy={h - (v / max) * (h - 4) - 2} r="1.5" fill={stroke} />
      ))}
    </svg>
  );
}

// -------------------- Channel Attribution --------------------

function ChannelPanel({ data }) {
  const channels = data.channels || [];
  const t = data.totals || {};
  const top = channels[0];

  return (
    <div data-testid="channel-panel">
      <div className="grid sm:grid-cols-3 gap-5 mb-8">
        <Card testid="chan-total-sold" icon={<Layers />} label="pieces sold" value={t.sold || 0} sub={`gross margin ${formatMoney(t.gross_margin || 0)}`} />
        <Card testid="chan-total-rev" icon={<Wallet />} label="lifetime revenue" value={formatMoney(t.revenue || 0)} sub="across all channels" />
        <Card testid="chan-top" icon={<ArrowRightLeft />} label="top revenue channel" value={top ? `${top.share_of_revenue_pct}%` : "—"} sub={top ? top.channel.toLowerCase() : "no sales yet"} />
      </div>

      <div className="bg-linen border border-taupe/50 p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-serif text-2xl lowercase text-charcoal">by channel</h3>
          <span className="editorial-label">which channel is actually paying the rent</span>
        </div>
        {channels.length === 0 ? (
          <p className="editorial-label">no sold pieces yet — the channel funnel activates once sales are logged.</p>
        ) : (
          <>
            {/* Revenue distribution bar */}
            <div className="w-full h-8 flex overflow-hidden border border-taupe/30 rounded-sm mb-2" data-testid="chan-share-bar">
              {channels.map((c, idx) => {
                const palette = ["#4b7f52", "#c9a94b", "#c67d33", "#7a5c38", "#b8a884", "#4b2a2a"];
                return c.share_of_revenue_pct > 0 ? (
                  <div
                    key={c.channel}
                    title={`${c.channel} · ${c.share_of_revenue_pct}% · ${formatMoney(c.revenue)}`}
                    style={{ width: `${c.share_of_revenue_pct}%`, backgroundColor: palette[idx % palette.length] }}
                    className="h-full transition-all"
                  />
                ) : null;
              })}
            </div>
            <p className="editorial-label text-taupe mb-6">share of revenue · hover to see the split</p>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-taupe">
                  <th className="editorial-label py-2">channel</th>
                  <th className="editorial-label py-2 text-right">sold</th>
                  <th className="editorial-label py-2 text-right">avg ticket</th>
                  <th className="editorial-label py-2 text-right">revenue</th>
                  <th className="editorial-label py-2 text-right">margin</th>
                  <th className="editorial-label py-2 text-right">margin %</th>
                  <th className="editorial-label py-2 text-right">share</th>
                  <th className="editorial-label py-2 text-right">avg turn</th>
                  <th className="editorial-label py-2 text-right">repriced</th>
                </tr>
              </thead>
              <tbody>
                {channels.map((c) => (
                  <tr key={c.channel} className="border-t border-taupe/30" data-testid={`chan-row-${c.channel}`}>
                    <td className="py-2 font-serif text-base lowercase text-charcoal">{c.channel}</td>
                    <td className="py-2 text-right tabular-nums text-charcoal">{c.sold}</td>
                    <td className="py-2 text-right tabular-nums text-taupe">{formatMoney(c.avg_ticket)}</td>
                    <td className="py-2 text-right tabular-nums text-charcoal">{formatMoney(c.revenue)}</td>
                    <td className={`py-2 text-right tabular-nums ${c.gross_margin >= 0 ? "text-emerald-800" : "text-red-700"}`}>{formatMoney(c.gross_margin)}</td>
                    <td className={`py-2 text-right tabular-nums ${c.margin_pct >= 30 ? "text-emerald-800" : c.margin_pct >= 0 ? "text-charcoal" : "text-red-700"}`}>{c.margin_pct}%</td>
                    <td className="py-2 text-right tabular-nums text-brass">{c.share_of_revenue_pct}%</td>
                    <td className="py-2 text-right tabular-nums text-taupe">{c.avg_turn_days || "—"}d</td>
                    <td className="py-2 text-right tabular-nums text-taupe" title="pieces that sold after being repriced">
                      {c.repriced_sold > 0 ? c.repriced_sold : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="editorial-label mt-4 text-taupe">&ldquo;repriced&rdquo; = pieces sold after a markdown was applied — validates whether the reprice engine is actually moving stock.</p>
          </>
        )}
      </div>
    </div>
  );
}

